import { useMemo } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { usePracticeDiscountRules, useSetPracticeRuleEligibility } from '../../../hooks/useBilling';
import { useToast } from '../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../utils/apiError';
import { formatMoneyDollars } from '../../../utils/money';
import type { PracticeDiscountRule } from '../../../services/billing';
import { useEscapeKey } from '../../../hooks/useEscapeKey';

// Practice-side eligibility editor: every discount rule in the catalog,
// grouped by product, with a toggle for whether THIS practice gets it.
// The product-side counterpart is the roster inside the rule editor.

interface PracticeEligibilityModalProps {
    practiceId: string;
    practiceName: string;
    onClose: () => void;
}

function ruleBadgeLabel(rule: PracticeDiscountRule): string {
    return rule.discountType === 'percentage'
        ? `-${rule.discountValue}%`
        : `-${formatMoneyDollars(rule.discountValue)}`;
}

export function PracticeEligibilityModal({
    practiceId,
    practiceName,
    onClose
}: PracticeEligibilityModalProps) {
    const { showToast } = useToast();
    const { rules, isLoading } = usePracticeDiscountRules(practiceId);
    const { mutateAsync: setEligibility, isPending } = useSetPracticeRuleEligibility();

    useEscapeKey(onClose, !isPending);

    const grouped = useMemo(() => {
        const map = new Map<string, PracticeDiscountRule[]>();
        for (const rule of rules) {
            const group = map.get(rule.productName) ?? [];
            group.push(rule);
            map.set(rule.productName, group);
        }
        return [...map.entries()];
    }, [rules]);

    const eligibleCount = rules.filter((rule) => rule.eligible).length;

    const toggle = async (rule: PracticeDiscountRule) => {
        try {
            await setEligibility({ practiceId, ruleId: rule.id, eligible: !rule.eligible });
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to update eligibility'), 'error');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center p-6">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[640px] max-h-[85vh] flex flex-col overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">Discount eligibility</h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {practiceName} · eligible for {eligibleCount} of {rules.length} rules
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col gap-5">
                    {isLoading ? (
                        <div className="flex items-center justify-center gap-2 text-gray-400 py-10">
                            <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                            <span className="text-sm">Loading rules…</span>
                        </div>
                    ) : grouped.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-10">
                            No discount rules exist yet - create them from the products catalog.
                        </p>
                    ) : (
                        grouped.map(([productName, productRules]) => (
                            <div key={productName} className="flex flex-col gap-1.5">
                                <span className="text-[12px] font-bold tracking-[0.005em] text-neutral-500 uppercase">
                                    {productName}
                                </span>
                                <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
                                    {productRules.map((rule) => (
                                        <label
                                            key={rule.id}
                                            className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 ${isPending ? 'opacity-60 pointer-events-none' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={rule.eligible}
                                                onChange={() => {
                                                    void toggle(rule);
                                                }}
                                                className="sr-only"
                                            />
                                            <span
                                                className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${rule.eligible ? 'bg-gray-900' : 'border border-gray-300'}`}
                                            >
                                                {rule.eligible && (
                                                    <CheckIcon
                                                        className="h-3 w-3 text-white"
                                                        strokeWidth={3}
                                                    />
                                                )}
                                            </span>
                                            <span className="inline-flex text-[11px] font-bold px-2 py-0.5 rounded-md bg-green-50 text-green-600 tabular-nums shrink-0">
                                                {ruleBadgeLabel(rule)}
                                            </span>
                                            <span className="text-sm font-medium text-gray-800 flex-1 truncate">
                                                {rule.name}
                                            </span>
                                            {rule.appliesToVersionLabel && (
                                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 shrink-0">
                                                    {rule.appliesToVersionLabel}
                                                </span>
                                            )}
                                            {!rule.active && (
                                                <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 shrink-0">
                                                    inactive
                                                </span>
                                            )}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}
